import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

interface QuizSettings {
  time_limit_minutes: number;
  passing_score: number;
  allow_retake: boolean;
  show_correct_answers: boolean;
}

interface QuizSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: QuizSettings) => void;
  currentSettings?: QuizSettings;
  translations?: {
    title?: string;
    timeLimit?: string;
    passingScore?: string;
    allowRetake?: string;
    showCorrectAnswers?: string;
    cancel?: string;
    save?: string;
    minutes?: string;
    percent?: string;
    [key: string]: string | undefined;
  };
}

export default function QuizSettingsModal({ 
  isOpen, 
  onClose, 
  onSave, 
  currentSettings,
  translations = {} 
}: QuizSettingsModalProps) {
  const [settings, setSettings] = useState<QuizSettings>({
    time_limit_minutes: 30,
    passing_score: 70,
    allow_retake: true,
    show_correct_answers: true,
  });

  // Update settings when currentSettings changes
  useEffect(() => {
    if (currentSettings) {
      setSettings(currentSettings);
    }
  }, [currentSettings]);

  // Early return if modal is not open
  if (!isOpen) return null;

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const handleTimeLimitChange = (value: string) => {
    const minutes = parseInt(value) || 0;
    setSettings(prev => ({ ...prev, time_limit_minutes: minutes }));
  };

  const handlePassingScoreChange = (value: string) => {
    const score = parseInt(value) || 0;
    setSettings(prev => ({ ...prev, passing_score: Math.min(100, Math.max(0, score)) }));
  };

  const handleRetakeChange = (checked: boolean) => {
    setSettings(prev => ({ ...prev, allow_retake: checked }));
  };

  const handleShowAnswersChange = (checked: boolean) => {
    setSettings(prev => ({ ...prev, show_correct_answers: checked }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{translations.title || 'Cài đặt Quiz'}</CardTitle>
          <CardDescription>
            Điều chỉnh các thông số cho bài kiểm tra này
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="time-limit" className="text-sm font-medium">
                {translations.timeLimit || 'Thời gian làm bài'}
              </label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  id="time-limit"
                  type="number"
                  min="1"
                  max="300"
                  value={settings.time_limit_minutes}
                  onChange={(e) => handleTimeLimitChange(e.target.value)}
                  className="flex-1"
                />
                <span className="text-sm text-gray-500">
                  {translations.minutes || 'phút'}
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="passing-score" className="text-sm font-medium">
                {translations.passingScore || 'Điểm đạt'}
              </label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  id="passing-score"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.passing_score}
                  onChange={(e) => handlePassingScoreChange(e.target.value)}
                  className="flex-1"
                />
                <span className="text-sm text-gray-500">
                  {translations.percent || '%'}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow-retake"
                  checked={settings.allow_retake}
                  onCheckedChange={handleRetakeChange}
                />
                <label htmlFor="allow-retake" className="text-sm">
                  {translations.allowRetake || 'Cho phép làm lại'}
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show-answers"
                  checked={settings.show_correct_answers}
                  onCheckedChange={handleShowAnswersChange}
                />
                <label htmlFor="show-answers" className="text-sm">
                  {translations.showCorrectAnswers || 'Hiển thị đáp án sau khi hoàn thành'}
                </label>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            {translations.cancel || 'Hủy'}
          </Button>
          <Button onClick={handleSave}>
            {translations.save || 'Lưu'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
